import { AppComponent } from "@reporting/rp-app-host";
import { html } from "lit";
import { customElement, query } from "lit/decorators.js";
import { AnalyticsApplication, Filter, FilterState, MultiFilterContainer, getContextIdFromComponentId } from "../..";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { map } from "lit/directives/map.js";
import { MenuEvent, Overlay, utils } from "@omegagrid/core";
import { cloneContext, isMultiContext } from "../../common";
import { msg } from "@omegagrid/localize";
import { style } from "./filterContainer.style";
import { ModuleDefinition } from "@reporting/rp-types/definition";
import { DefaultFilterAdapter, FilterAdapter } from "../../adapters";
import { BucketEvent } from "@omegagrid/bucket";
import { ContextData, MultiContextData } from "@reporting/rp-types/analytics";
import { openContextJson, openContextSql, openReport, getContext, ReportTarget } from "../../actions";
import rp from '@reporting/rp-app-host';

export type FilterContainerState = {
	module?: ModuleCode;
	idContext?: number;
	filter?: FilterState;
}

@customElement('rp-filtercontainer')
export class FilterContainer extends AppComponent<AnalyticsApplication, FilterContainerState> {

	static styles = [AppComponent.styles, style];

	@query('og-overlay')
	loader: Overlay;

	@query('rp-filter')
	filter: Filter;

	@query('rp-multifiltercontainer')
	multiFilter: MultiFilterContainer;

	showLoader = () => this.loader.show(this);
	hideLoader = () => this.loader.hide();

	get app() { return rp.applications.get('analytics') as AnalyticsApplication }
	get service() { return rp.ms('analytics') }
	get moduleDefinition() { return this.app.modules.get(this.state?.module) ?? null }
	get contextBroker() { return this.app.contextBroker }

	private _itemIndex: number;
	private _loadedContextKey: string;

	async getContext() {
		if (!this.state?.module) return null;

		// open dashboards live in a store: read the draft so the sidebar sees
		// unsaved edits; writes go back through the store (multiFilterContainer)
		const store = this.app.getReportStore(this.state.idContext);
		if (store) return store.context;

		const context = await this.contextBroker.getContext({
			id_context: this.state.idContext,
			module: this.state.idContext ? null : this.state.module
		});

		return context ? cloneContext(context) : null;
	}

	async getContextData() {
		return (await this.getContext())?.context;
	}

	_onHostTabActivate = (e: BucketEvent) => {
		// TODO: move this logic to main AnalyticsApplication class
		// follow the activated report container tab
		const idContext = getContextIdFromComponentId(e.tab?.id);
		if (idContext) this.updateState({idContext}, true);
	}

	connectedCallback() {
		super.connectedCallback();
		rp.host.addEventListener('bucket.activate', this._onHostTabActivate);

		// if a report container tab is already active, follow it
		const idContext = getContextIdFromComponentId(rp.host?.bucket?.activeComponentId);
		if (idContext) this.updateState({idContext}, true);
	}

	disconnectedCallback() {
		rp.host.removeEventListener('bucket.activate', this._onHostTabActivate);
	}

	async willUpdate(props: Map<string, any>) {
		this.state ??= {};
		this.state.module ??= [...this.app.modules.keys()][0];
		this.state.idContext ??= 0;
	}

	async updated() {
		// reload the context only when module/idContext actually changed,
		// otherwise every update cycle would re-fetch and cascade re-renders
		const contextKey = `${this.state?.module}:${this.state?.idContext}`;
		if (contextKey == this._loadedContextKey && this._itemIndex == null) return;
		this._loadedContextKey = contextKey;

		this.showLoader();
		const context = await this.getContext();
		const contextData = context?.context;

		// the single filter sections mutate contextData while editing - hand
		// them a working copy (the context may be an immutable store draft)
		if (this.filter) this.filter.contextData = cloneContext(contextData) as ContextData;
		if (this.multiFilter) {
			this.multiFilter.context = context;
			if (this._itemIndex != null) {
				this.multiFilter.contextItemIndex = this._itemIndex;
				this._itemIndex = null;
			}
		}

		if (this.state?.module != contextData?.module) {
			this.updateState({module: contextData?.module as ModuleCode}, true);
		}
		this.hideLoader();
	}

	_onTabClick = async (e: PointerEvent) => {
		const m = (e.target as HTMLElement).dataset.module as ModuleCode;
		if (m == this.state.module) return;
		this.updateState({module: m, idContext: 0}, true);
	}

	async openReport(target: ReportTarget = 'active_tab') {
		this.showLoader();

		const context = await getContext({
			id_context: this.state.idContext,
			module: this.state.module,
			context: this.filter.contextData,
		});

		this.updateState({idContext: context.id_context});
		await openReport(context.id_context, { context, target });

		this.hideLoader();
	}

	async openQuery() {
		this.showLoader();
		await openContextSql({
			id_context: this.state.idContext,
			module: this.state.module,
			context: this.filter.contextData,
		});
		this.hideLoader();
	}

	async openContext() {
		this.showLoader();
		await openContextJson({
			id_context: this.state.idContext,
			module: this.state.module,
			context: this.filter.contextData,
		});
		this.hideLoader();
	}

	async openFilter(idContext: number, itemIndex?: number) {
		this.setState({idContext});
		this.requestUpdate();
		this._itemIndex = itemIndex;
	}

	/**
	 * The followed report got a new id (save as new). Adopt it without
	 * reloading the context - the sections keep the working copy the user
	 * may be editing (its content equals the saved context anyway).
	 */
	renameContext(oldId: number, newId: number) {
		if (this.state?.idContext !== oldId) return;
		this._loadedContextKey = `${this.state.module}:${newId}`;
		this.updateState({idContext: newId}, true);
	}

	executeMenuHandlers: Record<string, () => void> = {
		openQuery: () => this.openQuery(),
		openContext: () => this.openContext(),
	}

	_adapters = new Map<ModuleCode, FilterAdapter>();

	getFilterAdapter() {
		if (!this.moduleDefinition) return null;
		if (!this._adapters.has(this.state.module)) {
			this._adapters.set(this.state.module, new DefaultFilterAdapter(this.moduleDefinition));
		}
		return this._adapters.get(this.state.module);
	}

	getVisibleModules() {
		return [...this.app.modules.values()].filter(
			m => ['F', 'I', 'A', 'C'].includes(m.module) || (isMultiContext(m.module) && m.module == this.state?.module)
		);
	}

	get isMultiContext() {
		return isMultiContext(this.state?.module);
	}

	renderHead = () => {
		const visiblerModules = this.getVisibleModules();
		if (visiblerModules.length <= 1) return null;
		return html`
			<div class="head">
				${map(visiblerModules, (module: ModuleDefinition) => html`
					<og-bucket-button
						class="btn-module"
						.text="${module.name?.toUpperCase()}"
						.icon="${{ icon: 'cube', color: module.module == this.state.module ? null : module.color }}"
						data-module="${module.module}"
						@click=${this._onTabClick}
						?active="${module.module == this.state.module}">
					</og-bucket-button>
				`)}
			</div>
		`;
	};

	renderControls = () => this.isMultiContext ? html`` : html`
		<div class="controls">
			<og-button
				class="btn-primary"
				color="green"
				@click="${() => this.openReport()}">
				<div slot="content" style="display: flex; align-items: center; justify-content: center; gap: 5px;">
					<og-icon icon="wand-magic-sparkles"></og-icon> ${msg('Show')}
				</div>
			</og-button>

			<og-button
				class="btn-secondary"
				color="green"
				icon="window-restore"
				.title="${msg('Open in new tab')}"
				@click="${() => this.openReport('new_tab')}">
			</og-button>

			${rp.userProvider.hasRight('etl') ? html`
				<og-dropdown-menu
					dynamicWidth
					color="green"
					@menu.select="${(e: MenuEvent) => this.executeMenuHandlers[e.item.key]()}"
					.items="${[
						{key: 'openContext', value: 'Show JSON'},
						{key: 'openQuery', value: 'Show SQL'},
					]}">
				</og-dropdown-menu>
			` : null}
		</div>
	`;

	renderBody = () => html`
		<div class="body">
			${this.isMultiContext ? html`
				<rp-multifiltercontainer
					.state="${this.state}"
					@state="${() => this.updateState(this.multiFilter.state)}">
				</rp-multifiltercontainer>
			` : html`
				<og-container>
					<rp-filter
						slot="content"
						.state="${this.state?.filter}"
						@state="${() => this.updateState({filter: this.filter.state})}"
						.adapter="${this.getFilterAdapter()}"
						.module="${this.moduleDefinition}">
					</rp-filter>
				</og-container>
			`}
		</div>
	`;

	render = () => html`
		<og-overlay defaultLoader></og-overlay>

		${this.getVisibleModules().length ? html`
			${this.renderHead()}
			${this.renderControls()}
			${this.renderBody()}
		` : html`
			<div style="padding: 5px">
				<og-alert type="warning" .text="${msg('No data modules available')}"></og-alert>
			</div>
		`}
	`;

}
